import * as yup from 'yup';
import { ChangeEvent } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetLotteriesForEventQuery, GetLotteryQuery as GetLottery } from '__generated__/graphql';

import { TranslatableEditor } from '../../Translations/Inputs';
import { encodeUrl, getServerErrors } from '../../../common/helpers';
import { replacementKeys } from '../../Events/Event/Settings/EditEventConfirmationMessagesForm';
import { translationsForAttribute } from '../../Translations/helpers';
import MailEditorLayout from '../../Common/MailEditorLayout';
import UI from '../../../common/UI';
import useEditLotteryMutation from './useEditLotteryMutation';
import useLocale from '../../../common/useLocale';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';
import useValidation from '../../../common/useValidation';

type Lottery = GetLottery['project']['lottery'];

const editLotteryInfoSchema = yup.object().shape({
  title: yup.string().ensure().required(),
  ticket_id: yup.string().ensure().required(),
  promotion_id: yup.string().ensure().required(),
});

export interface EditLotteryInfoFormProps {
  lottery: Lottery;
  event: GetLotteriesForEventQuery['project']['event'];
}

const EditLotteryInfoForm = ({ lottery, event }: EditLotteryInfoFormProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const project = useProject();
  const formatTimeSlot = useTimeSlotFormatter();

  const getValues = (lottery: Lottery) => ({
    title: lottery.title,
    ticket_id: lottery.ticket.id,
    promotion_id: lottery.promotion.id,
    time_slot_id: lottery.time_slot?.id,
    confirmation_mail_text: lottery.confirmation_mail_text,
    confirmation_mail_text_translations: translationsForAttribute(lottery.translations, 'confirmation_mail_text'),
  });

  const form = useForm({
    values: getValues(lottery),
  });

  useNavigationPrompt(form.changed());

  const tickets = event.tickets.filter((ticket) => ticket.allow_lottery_confirmations);
  const selectedTicket = tickets.find((ticket) => ticket.id === form.values.ticket_id);
  const showPromotion = selectedTicket && selectedTicket.promotions.length > 1;
  const showTimeSlot = selectedTicket.time_slots.length > 1;

  const handleTicketChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const ticket = tickets.find((ticket) => ticket.id === changeEvent.target.value);

    if (!ticket) {
      return;
    }

    form.set((values) => ({
      ...values,
      ticket_id: ticket.id,
      promotion_id: ticket.promotions.length === 1 ? ticket.promotions[0].id : null,
      time_slot_id: null,
    }));
  };

  const handlePromotionChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const promotion = selectedTicket.promotions.find((promotion) => promotion.id === changeEvent.target.value);

    form.set((values) => ({
      ...values,
      promotion_id: promotion?.id,
    }));
  };

  const handleTimeSlotChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const timeSlot = selectedTicket.time_slots.find((timeSlot) => timeSlot.id === changeEvent.target.value);

    form.set((values) => ({
      ...values,
      time_slot_id: timeSlot?.id,
    }));
  };

  const [editLottery, { error, loading }] = useEditLotteryMutation({
    input: {
      id: lottery.id,
      info: form.values,
    },
    onSuccess: (lottery) => {
      form.reset(getValues(lottery));
    },
  });

  const { valid, errors } = useValidation({
    schema: editLotteryInfoSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editLottery()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('basic_info')}
          </UI.Legend>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="LotteryTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('for_internal_use_only')}
          </UI.InputDescription>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={255}
            id="LotteryTitle"
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.HR />

        <UI.Div>
          <UI.Legend>
            {t('lottery_for_ticket')}
          </UI.Legend>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.ticket_id, touched: form.getTouched('ticket_id') }}>
          <UI.InputLabel htmlFor="LotteryTicket">
            {t('ticket')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('lottery_ticket_description')}
          </UI.InputDescription>
          <UI.Select
            onChange={handleTicketChange}
            value={form.values.ticket_id}
            id="LotteryTicket"
            disabled={lottery.locked}
          >
            {!form.values.ticket_id && <option />}
            {tickets.map((ticket, index) => (
              <option value={ticket.id} key={index}>
                {ticket.title}
              </option>
            ))}
          </UI.Select>
          <UI.ErrorMessages attribute={t('ticket')} errors={errors.ticket_id} />
        </UI.InputGroup>

        {selectedTicket && (showPromotion || showTimeSlot) && (
          <UI.FormGrid sc={{ columns: showPromotion && showTimeSlot ? '1fr 1fr' : '1fr' }}>
            {showPromotion && (
              <UI.InputGroup sc={{ valid: !errors.promotion_id, touched: form.getTouched('promotion_id') }}>
                <UI.InputLabel htmlFor="LotteryPromotion">
                  {t('promotion')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.Select
                  onChange={handlePromotionChange}
                  value={form.values.promotion_id || ''}
                  id="LotteryPromotion"
                  disabled={lottery.locked}
                >
                  {!form.values.promotion_id && <option />}
                  {selectedTicket.promotions.map((promotion, index) => (
                    <option value={promotion.id} key={index}>
                      {promotion.title || t('standard')}
                      {' • '}
                      {formatCurrency(promotion.amount)}
                    </option>
                  ))}
                </UI.Select>
                <UI.ErrorMessages attribute={t('promotion')} errors={errors.promotion_id} />
              </UI.InputGroup>
            )}

            {showTimeSlot && (
              <UI.InputGroup sc={{ valid: !errors.time_slot_id, touched: form.getTouched('time_slot_id') }}>
                <UI.InputLabel htmlFor="LotteryTimeSlot">
                  {t('time_slot')}
                </UI.InputLabel>
                <UI.Select
                  onChange={handleTimeSlotChange}
                  value={form.values.time_slot_id || ''}
                  id="LotteryTimeSlot"
                  disabled={lottery.locked}
                >
                  <option value="">
                    {t('no_time_slot')}
                  </option>
                  {selectedTicket.time_slots.map((timeSlot, index) => (
                    <option value={timeSlot.id} key={index}>
                      {formatTimeSlot(timeSlot)}
                      {timeSlot.title && ` • ${timeSlot.title}`}
                    </option>
                  ))}
                </UI.Select>
                <UI.ErrorMessages attribute={t('time_slot')} errors={errors.time_slot_id} />
              </UI.InputGroup>
            )}
          </UI.FormGrid>
        )}

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('confirmation')}
          </UI.Legend>
          <UI.InputDescription>
            {t('lottery_confirmation_mail_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup
          sc={{ valid: !errors.confirmation_mail_text, touched: form.touched.confirmation_mail_text }}
        >
          <UI.InputLabel id="LotteryConfirmationMailLabel">
            {t('confirmation_mail')}
          </UI.InputLabel>
          <MailEditorLayout
            editor={(props) => (
              <TranslatableEditor
                form={form}
                errors={errors}
                name="confirmation_mail_text"
                defaultLocale={event.locale}
                title={t('confirmation_mail')}
                variables={replacementKeys}
                options="full"
                attributes={{
                  'aria-labelledby': 'LotteryConfirmationMailLabel',
                }}
                {...props}
              />
            )}
            value={form.values.confirmation_mail_text}
            event={event}
            showRegistrationSummary
            showOrderSummary
          />
          <UI.ErrorMessages
            attribute={t('confirmation_mail')}
            errors={errors.confirmation_mail_text}
          />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_lottery')}
          closeTo={`/${project.id}/participants/lotteries?${encodeUrl({ event: event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditLotteryInfoForm;
